/**
 * Media Session API utilities for mobile volume control
 */

interface MediaSessionMetadata {
  title?: string;
  artist?: string;
  album?: string;
  artwork?: MediaImage[];
}

let lastAbort: AbortController | null = null;

/**
 * Throttles Media Session position updates using requestAnimationFrame
 * to prevent excessive API calls from frequent timeupdate events
 */
const safePositionUpdate = (() => {
  let raf = 0; // Stores the current requestAnimationFrame ID
  return (
    ms: MediaSession & { setPositionState?: Function },
    el: HTMLMediaElement
  ) => {
    // Cancel any pending position update to avoid race conditions
    if (raf) cancelAnimationFrame(raf);

    // Schedule the position update for the next animation frame
    // This ensures updates are batched and don't overwhelm the Media Session API
    raf = requestAnimationFrame(() => {
      ms.setPositionState?.({
        duration: Number.isFinite(el.duration) ? el.duration : 0,
        playbackRate: el.playbackRate || 1,
        position: Number.isFinite(el.currentTime) ? el.currentTime : 0,
      });
    });
  };
})();

/**
 * Sets up Media Session API for the given media element
 * So OS can know the website is playing audio and use these action handlers for audio playback
 */
export const setupMediaSession = (
  mediaElement: HTMLMediaElement,
  metadata?: MediaSessionMetadata
): void => {
  if (!("mediaSession" in navigator)) {
    return;
  }

  const mS = navigator.mediaSession;

  // If a different media element was previously bound, remove its event listeners before binding the new one
  if (lastAbort) {
    lastAbort.abort();
    lastAbort = null;
  }
  lastAbort = new AbortController();
  const { signal } = lastAbort;

  // Set metadata
  mS.metadata = new MediaMetadata({
    title: metadata?.title || "Aveta Assistant",
    artist: metadata?.artist || "Aveta",
    album: metadata?.album || "Support Conversation",
    artwork: metadata?.artwork || [],
  });

  // Set action handlers
  mS.setActionHandler("play", () => mediaElement.play().catch(console.error));
  mS.setActionHandler("pause", () => mediaElement.pause());
  mS.setActionHandler("stop", () => {
    mediaElement.pause();
    mediaElement.currentTime = 0;
  });

  // Update playback state when media state changes
  const updatePlaybackState = () => {
    mediaElement.paused ? "paused" : "playing";
  };

  const onPlayPause = () => updatePlaybackState();
  const onTimeOrMeta = () => safePositionUpdate(mS as any, mediaElement);

  mediaElement.addEventListener("play", onPlayPause, { signal });
  mediaElement.addEventListener("pause", onPlayPause, { signal });
  mediaElement.addEventListener("loadedmetadata", onTimeOrMeta, { signal });
  mediaElement.addEventListener("timeupdate", onTimeOrMeta, { signal });

  // initial push
  updatePlaybackState();
  onTimeOrMeta();
};

export const cleanupMediaSession = (): void => {
  if (!("mediaSession" in navigator)) return;
  const mS = navigator.mediaSession;

  // Clear action handlers
  mS.setActionHandler("play", null);
  mS.setActionHandler("pause", null);
  mS.setActionHandler("stop", null);

  // Clear metadata
  mS.metadata = null;

  // Clear rAF
  if (lastAbort) {
    lastAbort.abort();
    lastAbort = null;
  }
};

export const ensureMediaSessionMetadata = (): void => {
  if (!("mediaSession" in navigator)) return;
  const ms = navigator.mediaSession;
  // only bump metadata if it changed
  const next = new MediaMetadata({
    title: "Aveta Assistant",
    artist: "AI Response",
    album: "Conversation",
    artwork: [],
  });
  if (
    !ms.metadata ||
    ms.metadata.title !== next.title ||
    ms.metadata.artist !== next.artist ||
    ms.metadata.album !== next.album
  ) {
    ms.metadata = next;
  }
};

/**
 * Disconnects media session API completely
 * Used when page becomes hidden or is being unloaded
 */
export const disconnectMediaSession = (): void => {
  if (!("mediaSession" in navigator)) return;
  const mS = navigator.mediaSession;

  // Clear action handlers
  mS.setActionHandler("play", null);
  mS.setActionHandler("pause", null);
  mS.setActionHandler("stop", null);

  // Clear metadata
  mS.metadata = null;

  // Clear rAF
  if (lastAbort) {
    lastAbort.abort();
    lastAbort = null;
  }
};
